import { useEffect, useState } from "react";
import { Polyline } from "react-leaflet";
import { Sparkles, Route, Clock } from "lucide-react";
import api from "@/lib/api";

/**
 * RoutePolyline — fetches an OSRM + GPT-5.2 optimised route for a ride
 * and renders it as a gold polyline. Returns null if the route service
 * is unavailable. Designed to be a child of <MapContainer>.
 */
export function RoutePolyline({ rideId }) {
  const [route, setRoute] = useState(null);
  useEffect(() => {
    if (!rideId) return;
    let cancel = false;
    api.get(`/chauffeur/rides/${rideId}/route`)
      .then((r) => { if (!cancel) setRoute(r.data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [rideId]);

  if (!route?.geometry?.length) return null;
  return (
    <Polyline positions={route.geometry} pathOptions={{ color: "#d4af37", weight: 5, opacity: 0.85 }} />
  );
}

/**
 * RouteSummary — small card showing AI-picked distance/duration/rationale.
 * Sits beside the map, NOT inside MapContainer.
 */
export function RouteSummary({ rideId }) {
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!rideId) return;
    let cancel = false;
    setLoading(true);
    api.get(`/chauffeur/rides/${rideId}/route`)
      .then((r) => { if (!cancel) setRoute(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [rideId]);

  if (loading) {
    return (
      <div data-testid="route-summary-loading" className="px-4 py-3 border border-gold/20 bg-gold/[0.03] rounded-md text-xs font-body text-white/50 animate-pulse">
        Computing best route…
      </div>
    );
  }
  if (!route?.ok) return null;
  return (
    <div data-testid="route-summary" className="p-4 border border-gold/30 bg-gold/5 rounded-md">
      <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold flex items-center gap-1 mb-2">
        <Sparkles size={10} /> AI-Optimised Route
      </p>
      <div className="flex items-center gap-4 text-white text-sm font-body">
        <span className="inline-flex items-center gap-1"><Route size={14} className="text-gold" /> {route.distance_km} km</span>
        <span className="inline-flex items-center gap-1"><Clock size={14} className="text-gold" /> {route.duration_minutes} min</span>
        {route.alternatives?.length > 0 && (
          <span className="text-white/40 text-[10px]">{route.alternatives.length} alts considered</span>
        )}
      </div>
      <p className="text-white/60 text-[11px] font-body mt-2">{route.ai_rationale}</p>
    </div>
  );
}
